'use client';

import React from 'react';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import {
  clickOutsideEventListener,
  useClickOutside,
} from '@hooks/useClickOutside';
import { LanguageTypes } from '@components/IntlProvider/IntlProvider';
import { getNewLanguageRoute } from '@utils/getAppRoute';
import { Mobile } from '@components/Mobile';
import { Desktop } from '@components/Desktop';
import SVGIcons from '@components/SVGIcon';
import { NoTranslateWrapper } from '@components/NoTranslateWrapper';
import { Link } from '@components/Link';
import { Popover } from '@components/Popover/Popover';

import cn from './style.module.scss';

interface LanguageDropdownProps {
  options: {
    label: string;
    value: LanguageTypes;
  }[];

  className?: string;

  listClassName?: string;

  variant?: 'dark' | 'light';
}

export function LanguageDropdown({
  options,
  className,
  listClassName,
  variant = 'dark',
}: LanguageDropdownProps): JSX.Element {
  const [popupVisible, setVisible] = React.useState(false);

  const popoverContentRef = React.useRef<HTMLDivElement | null>(null);

  const selectInputRef = React.useRef<HTMLDivElement | null>(null);

  const pathname = usePathname();

  const { lang } = useParams() as {
    lang: LanguageTypes;
  };

  const searchParams = useSearchParams();

  const query = Object.fromEntries(searchParams.entries());

  const handleClickOutside = React.useCallback(
    (event: MouseEvent) => {
      if (selectInputRef?.current?.contains(event.target as Node)) return;

      if (popupVisible) {
        setVisible(false);
      }
    },
    [popupVisible]
  );

  useClickOutside(
    popoverContentRef,
    handleClickOutside as clickOutsideEventListener
  );

  const handleClick = () => {
    setVisible((prev) => !prev);
  };

  const selectedLanguage =
    options.find((item) => item.value === lang) || options[0];

  return (
    <div
      ref={popoverContentRef}
      className={`
        ${cn.languageDropdownWrapper}
        ${cn[variant]}
        ${variant === 'light' ? cn.dropdownLight : ''}
        ${className || ''}
      `}
    >
      <Popover
        visible={popupVisible}
        enablePortal={false}
        content={
          <ul
            className={`${cn.languageDropdownBlock}${listClassName ? ` ${listClassName}` : ''}`}
          >
            {options.map((item) => {
              const isActive = item.value === lang;

              return (
                <li className={cn.languageDropdownBlock__list} key={item.value}>
                  <Link
                    className={`${cn.languageDropdownLink} ${isActive ? cn.activeItem : ''}`}
                    query={query}
                    url={getNewLanguageRoute(pathname, item.value)}
                  >
                    <Desktop>
                      <NoTranslateWrapper>{item.label}</NoTranslateWrapper>
                    </Desktop>
                    <Mobile>
                      <NoTranslateWrapper>{item.value}</NoTranslateWrapper>
                    </Mobile>
                  </Link>
                </li>
              );
            })}
          </ul>
        }
      >
        <div
          ref={selectInputRef}
          className={`
            ${cn.languageDropdownElement}
            ${popupVisible ? cn.openBox : ''}
          `}
          onClick={handleClick}
        >
          <Desktop>
            <NoTranslateWrapper>{selectedLanguage?.label}</NoTranslateWrapper>
          </Desktop>

          <Mobile>
            <NoTranslateWrapper>{lang}</NoTranslateWrapper>
          </Mobile>

          <span className={cn.dropArrowIcon}>
            <SVGIcons type="rightarrow-svg" />
          </span>
        </div>
      </Popover>
    </div>
  );
}
